import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import Backend from 'i18next-http-backend'

// ─── SWIM i18n initialisation ─────────────────────────────────────────────────
// Follows the same pattern as OTRA (verified 2026-03-16).
//
// Translation files live in: /public/locales/{lng}/{namespace}.json
// Loaded lazily over HTTP — not bundled. New languages = new folder, zero code change.
//
// ⚠️ Namespace scoping rule (KodeArk: i18n Double-Namespacing Bug):
//   useTranslation('offers') → t('title')         ✓  resolves to offers.title
//   useTranslation('offers') → t('offers.title')  ✗  resolves to offers.offers.title
//   useTranslation()         → t('offers.title')  ✓  global scope
//
// All aria-label, placeholder, title, and alt attributes go through t() —
// even if we are only shipping English today.

i18n
  .use(Backend)           // Load translations via HTTP from /public/locales/
  .use(LanguageDetector)  // Detect from localStorage → navigator → htmlTag
  .use(initReactI18next)  // Bind to React
  .init({
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    supportedLngs: ['en'],    // expand when adding languages
    fallbackLng: 'en',
    defaultNS: 'common',

    // All SWIM namespaces — add here when a new domain ships UI
    ns: [
      'common',       // shared strings: actions, labels, errors, pagination
      'navigation',   // nav items, breadcrumbs, bottom bar labels
      'auth',         // login, signup, password, session messages
      'offers',       // MBO titles, descriptions, CTA labels, status labels
      'drops',        // drop balance, purchase, transaction history
      'claims',       // claim flow, confirmation, status, dispute
      'account',      // profile, settings, preferences
      'pacer',        // Pacer orb, panel, voice prompts
      'merchant',     // merchant store page, about, contact
      'errors',       // error boundaries, 404, network error, server error
    ],

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'swim-language',
      caches: ['localStorage'],
    },

    react: {
      useSuspense: true,  // React Suspense handles translation loading — no flicker
    },

    interpolation: {
      escapeValue: false,  // React escapes values — no double-escaping
      formatSeparator: ',',
    },

    debug: import.meta.env.DEV,

    // Ensure i18n does not release the Suspense gate until all namespaces are loaded.
    // Without this, the HTTP backend may still be fetching when React first renders —
    // causing t() to return keys instead of values.
    initImmediate: false,
  })

// ─── RTL support ──────────────────────────────────────────────────────────────
// When a RTL language is detected, flip the document direction.
// SWIM currently ships LTR only, but the hook is live for future expansion.
const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur', 'ps', 'sd']

i18n.on('languageChanged', (lng) => {
  const dir = RTL_LANGUAGES.includes(lng.split('-')[0]) ? 'rtl' : 'ltr'
  document.documentElement.setAttribute('dir', dir)
  document.documentElement.setAttribute('lang', lng)
})

export default i18n

export const changeLanguage = (lng: string) => i18n.changeLanguage(lng)
export const currentLanguage = () => i18n.language ?? 'en'

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
] as const

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]['code']
